/**
 * Shared presentational components, recreated from the design handoff.
 *
 * Copy that is visitor-facing lives here or in the screens; scoring vocabulary
 * (band labels, category names, evidence ladder) comes from @shared/config so
 * the UI and the Scorecard PDF can never drift apart.
 */

import type { ReactNode } from 'react';

import { EVIDENCE_LADDER, OFFERING } from '@shared/config.js';
import {
  DEPTH_LEVELS,
  EVIDENCE_TYPES,
  INSTANT_SCAN_DEPTH,
  MANAGEMENT_SUMMARY,
  MANAGEMENT_SUMMARY_INVITATION,
  SPL_LEVELS,
  SPL_SENTENCE,
  splDef,
} from '@shared/ndss-model.js';
import type { ReportSection } from '@shared/report-sections.js';
import type { Band, CategoryResult, ScanResult } from '@shared/types.js';

/* ------------------------------------------------------------------ */

export function BrandBar({ tag }: { tag: string }) {
  return (
    <header className="brandbar">
      {/* NTT DATA is always capitals and never breaks across a line. */}
      <img src="/assets/logo-future-blue.svg" alt="NTT DATA" />
      <span className="brand-tag">{tag}</span>
    </header>
  );
}

/* ------------------------------------------------------------------ */

const RING_RADIUS = 80;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function ScoreRing({ score }: { score: number }) {
  const offset = RING_CIRCUMFERENCE * (1 - score / 100);
  return (
    <div className="ring">
      <svg width="180" height="180" viewBox="0 0 180 180" aria-hidden="true" focusable="false">
        <circle cx="90" cy="90" r={RING_RADIUS} fill="none" stroke="var(--gray-50)" strokeWidth="12" />
        <circle
          className="ring-arc"
          cx="90"
          cy="90"
          r={RING_RADIUS}
          fill="none"
          stroke="var(--future-blue)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE.toFixed(1)}
          strokeDashoffset={offset.toFixed(1)}
        />
      </svg>
      <div className="ring-num">
        <b>{score}</b>
        <small>/ 100</small>
      </div>
    </div>
  );
}

/* ---------------- SPL — the full NDSS posture level ---------------- */

/**
 * SPL chip, matching the full NDSS. This is the visitor-facing headline as of
 * the 2026-07-19 decision to adopt SPL levels rather than neutral bands.
 * The level always appears with its label, never colour alone.
 */
export function SplChip({ level, size = 'md' }: { level: 0 | 1 | 2 | 3 | 4; size?: 'md' | 'lg' }) {
  const def = splDef(level);
  return (
    <span className={`spl-chip spl-chip--${size}`} style={{ ['--spl-color' as string]: def.color }}>
      <span className="spl-chip-id">{def.id}</span>
      <span className="spl-chip-label">{def.label}</span>
    </span>
  );
}

/** The five-rung SPL ladder, with the visitor's rung marked. */
export function SplLadder({ level }: { level: 0 | 1 | 2 | 3 | 4 }) {
  return (
    <ol className="spl-ladder" aria-label="Sovereignty Posture Levels">
      {SPL_LEVELS.map((rung) => {
        const on = rung.level === level;
        return (
          <li
            key={rung.id}
            className={`spl-rung${on ? ' on' : ''}`}
            style={on ? { ['--spl-color' as string]: rung.color } : undefined}
            aria-current={on ? 'step' : undefined}
          >
            <span className="spl-rung-id">{rung.id}</span>
            <span className="spl-rung-label">{rung.label}</span>
            <span className="spl-rung-range">
              {rung.scoreMin}–{rung.scoreMax}
            </span>
            {on && <span className="sr-only"> — your level</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function SplSentence({ level }: { level: 0 | 1 | 2 | 3 | 4 }) {
  return <p className="interpretation">{SPL_SENTENCE[level]}</p>;
}

/**
 * A real NDSS report section that has no data at this depth.
 *
 * It sits at its TRUE position in the report — not banished to an appendix —
 * so the Scorecard reads in the same order as a full NDSS report and the
 * absence is visible exactly where the content would be. It keeps the section's
 * own eyebrow and title, states plainly that there is no data and from which
 * depth it becomes available, and says what would fill it.
 *
 * Nothing is blurred or teased. There is simply nothing there yet, and the
 * card says so.
 */
export function EmptySection({ section }: { section: ReportSection }) {
  return (
    <section className="card card--empty" aria-labelledby={`${section.id}-heading`}>
      <div className="summary-head">
        <div>
          <h3 className="card-eyebrow card-eyebrow--muted">{section.eyebrow}</h3>
          <h2 className="card-title card-title--muted" id={`${section.id}-heading`}>
            {section.title}
          </h2>
        </div>
        <span className="gap-badge">From {section.unlockedAt}</span>
      </div>

      <div className="empty-state">
        <span className="empty-state-mark" aria-hidden="true" />
        <p className="empty-state-text">
          <b>No data at this depth.</b> {section.why}
        </p>
      </div>

      {section.deepScanAdds && (
        <p className="gap-adds">
          <b>Available from {section.unlockedAt}:</b> {section.deepScanAdds}
        </p>
      )}
    </section>
  );
}

/**
 * A note appended to a section that IS rendered, but more simply than the full
 * NDSS would render it. Sits inside the real widget, under the real data.
 */
export function PartialNote({ section }: { section: ReportSection }) {
  return (
    <p className="partial-note">
      <b>Simplified at this depth.</b> {section.why}{' '}
      <span className="partial-note-adds">
        From {section.unlockedAt}: {section.deepScanAdds}
      </span>
    </p>
  );
}

/**
 * Management summary — one of five standard texts, chosen by overall SPL.
 *
 * Labelled "generic to your posture level" in the UI itself, so nobody mistakes
 * it for analysis of their organisation. The invitation to talk sits directly
 * beneath it, which is the natural place to offer it: the visitor has just read
 * what their level means and will want to know what to do about it.
 */
export function ManagementSummary({ level }: { level: 0 | 1 | 2 | 3 | 4 }) {
  return (
    <section className="card" aria-labelledby="summary-heading">
      <div className="summary-head">
        <div>
          <h3 className="card-eyebrow">Management summary</h3>
          <h2 className="card-title" id="summary-heading">
            What the board needs to know
          </h2>
        </div>
        <span className="gap-badge">Generic to your level</span>
      </div>

      <p className="summary-text">{MANAGEMENT_SUMMARY[level]}</p>

      <div className="summary-invite">
        <p className="summary-invite-text">{MANAGEMENT_SUMMARY_INVITATION}</p>
        <a
          className="btn btn-secondary"
          href={`mailto:${OFFERING.contactEmail}?subject=${encodeURIComponent(
            'Instant Scan result — 30-minute conversation',
          )}`}
        >
          Talk it through with us
        </a>
      </div>
    </section>
  );
}

/** Assessment depth: where this result sits on L1–L4 and E1–E4. */
export function DepthPanel({ answered, total }: { answered: number; total: number }) {
  return (
    <section className="conf" aria-labelledby="depth-heading">
      <div className="ev-head">
        <h2 className="ev-title" id="depth-heading">
          How well-supported is the score?
        </h2>
        <span className="ev-badge">L1 · E1 / C1</span>
      </div>
      <p className="ev-body">
        Depth is the <b>weakest link</b>: an assessment qualifies for a level only if every
        answered control meets its evidence floor. All {answered} of {total} answers here are
        self-declared, so 100% of controls rest on <b>E1</b> — the lowest of four evidence types —
        corroborated by a single source (<b>C1</b>).
      </p>

      <ol className="depth-ladder">
        {DEPTH_LEVELS.map((rung) => (
          <li
            key={rung.id}
            className={`depth-rung${rung.id === INSTANT_SCAN_DEPTH ? ' on' : ''}`}
            aria-current={rung.id === INSTANT_SCAN_DEPTH ? 'step' : undefined}
          >
            <span className="depth-id">{rung.id}</span>
            <span className="depth-name">{rung.name}</span>
            <span className="depth-what">{rung.whatWeDo}</span>
            <span className="depth-tier">{rung.tier}</span>
          </li>
        ))}
      </ol>

      <div className="ev-dist">
        {[...EVIDENCE_TYPES].reverse().map((evidence) => {
          const pct = evidence.id === 'E1' ? 100 : 0;
          return (
            <div className="ev-row" key={evidence.id}>
              <span className="ev-id">{evidence.id}</span>
              <span className="ev-label">{evidence.label}</span>
              <div className="ev-track" aria-hidden="true">
                <div className="ev-bar" style={{ width: `${pct}%` }} />
              </div>
              <span className="ev-pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** What's working / What needs work, as the full NDSS splits them. */
export function PostureColumns({ result }: { result: ScanResult }) {
  const sorted = [...result.categories].sort((a, b) => b.score - a.score);
  const working = sorted.slice(0, 3);
  const needsWork = [...sorted].reverse().slice(0, 3);

  const row = (c: CategoryResult, i?: number) => (
    <li className="wrk-row" key={c.categoryId}>
      <span className="wrk-score">{Math.round(c.score)}</span>
      <span className="wrk-name">
        {i !== undefined && <b>#{i + 1} priority · </b>}
        {c.code} {c.shortTitle}
      </span>
      <SplChip level={c.spl} />
    </li>
  );

  return (
    <div className="two-col">
      <section className="card">
        <h3 className="card-eyebrow">What's working</h3>
        <ul className="wrk-list">{working.map((c) => row(c))}</ul>
      </section>
      <section className="card">
        <h3 className="card-eyebrow">What needs work</h3>
        <ul className="wrk-list">{needsWork.map((c, i) => row(c, i))}</ul>
      </section>
    </div>
  );
}

/** Eight categories at a glance — the full NDSS's grid. */
export function EightAtAGlance({ result }: { result: ScanResult }) {
  return (
    <ul className="glance-grid">
      {result.categories.map((c) => (
        <li className="glance" key={c.categoryId} style={{ borderTopColor: c.code ? undefined : undefined }}>
          <div className="glance-top">
            <span className="glance-code">
              {c.code} · {c.weight}%
            </span>
            <SplChip level={c.spl} />
          </div>
          <div className="glance-score">{Math.round(c.score)}</div>
          <div className="glance-name">{c.shortTitle}</div>
          <div className="glance-complete">
            {c.answered} of {c.catalogueTotal} answered · mean {c.mean.toFixed(2)}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function BandChip({ band }: { band: Band }) {
  return (
    <span className="band-chip">
      <span className="band-dot" aria-hidden="true" />
      {band.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The evidence panel. This is the deliberate centrepiece of the score screen:
 * it names the E1 floor plainly rather than inventing a confidence number.
 * There is no "x/10" anywhere in this component, by design.
 */
export function EvidencePanel({ compact = false }: { compact?: boolean }) {
  return (
    <section className="conf" aria-labelledby="evidence-heading">
      <div className="ev-head">
        <h2 className="ev-title" id="evidence-heading">
          What this score is based on
        </h2>
        <span className="ev-badge">E1 · self-declared</span>
      </div>
      <p className="ev-body">
        Your answers alone — one respondent, no documents reviewed. In our evidence model that
        is <b>E1</b>, the lowest of four levels. A <b>Deep Scan</b> raises every assessed control
        to document-backed or two independent sources, adds multi-role interviews and technical
        inspection where access permits, and shows the evidence behind every category.
      </p>
      <ol className="ev-ladder">
        {EVIDENCE_LADDER.map((step) => (
          <li
            key={step.tier}
            className={['ev-step', step.state].filter(Boolean).join(' ')}
            aria-current={step.state === 'here' ? 'step' : undefined}
          >
            <div className="ev-barzone" aria-hidden="true">
              <div className="ev-fill" />
            </div>
            <div className="ev-tier">{compact ? step.shortTier : step.tier}</div>
            <div className="ev-flr">{compact ? step.floor : `${step.floor} · ${step.detail}`}</div>
            <div className={`ev-here${step.state === 'next' ? ' rec' : ''}`}>
              {step.state === 'here' ? 'You are here' : step.state === 'next' ? 'Recommended' : ''}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function TierLine() {
  return (
    <ol className="tier-line" aria-label="Full-Stack Sovereignty Scan tiers">
      {EVIDENCE_LADDER.map((step, i) => (
        // The ladder's "next" state renders as the turquoise "recommended" chip.
        <li key={step.tier} className={step.state === 'next' ? 'rec' : (step.state ?? undefined)}>
          {step.shortTier}
          {step.state === 'here' && <span className="sr-only"> — your current tier</span>}
          {step.state === 'next' && <span className="sr-only"> — recommended next step</span>}
          {i < EVIDENCE_LADDER.length - 1 && <span className="sr-only">, then </span>}
        </li>
      ))}
    </ol>
  );
}

/* ------------------------------------------------------------------ */

export function UnlockList({
  heading,
  items,
}: {
  heading?: string;
  items: readonly string[];
}) {
  return (
    <div className="unlock">
      {heading && <p className="unlock-h">{heading}</p>}
      <ul>
        {items.map((item) => (
          <li className="trade-item" key={item}>
            <span className="trade-check" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** Category bars. Every bar states its number and, where tagged, a word —
 *  meaning is never carried by the fill colour alone. */
export function CategoryBreakdown({ categories }: { categories: readonly CategoryResult[] }) {
  return (
    <ul className="cat-list cat-grid">
      {categories.map((category) => {
        const rounded = Math.round(category.score);
        return (
          <li className="cat-row" key={category.categoryId}>
            <span className="cat-name">{category.name}</span>
            <span className="cat-wt">{category.weight}%</span>
            <div className="cat-barwrap">
              <div className="cat-track" aria-hidden="true">
                <div className="cat-fill" style={{ width: `${rounded}%` }} />
              </div>
              <span className="cat-val">
                {rounded}
                <span className="sr-only"> out of 100</span>
              </span>
              {category.tag && (
                <span className={`cat-tag tag-${category.tag}`}>{category.tag}</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------ */

export function DeepScanPanel({ children }: { children?: ReactNode }) {
  return (
    <aside className="conf" aria-labelledby="next-step-heading">
      <p className="navy-panel-h" style={{ marginBottom: 6 }}>
        Your recommended next step
      </p>
      <h2
        id="next-step-heading"
        style={{ fontSize: 19, fontWeight: 600, color: 'var(--white)', margin: '0 0 8px' }}
      >
        A Deep Scan
      </h2>
      <p className="ev-body" style={{ marginBottom: 16 }}>
        It raises every assessed control from E1 to E2 — document-backed or two independent
        sources — adds multi-role interviews and technical inspection, and shows the evidence
        behind each of the eight categories.
      </p>
      <div style={{ marginBottom: 18 }}>
        <TierLine />
      </div>
      <div className="contact-box">
        <div className="contact-box-l">Book a 30-minute intro</div>
        <div className="contact-box-v">
          <a href={`mailto:${OFFERING.contactEmail}`}>{OFFERING.contactEmail}</a>
        </div>
      </div>
      {children}
    </aside>
  );
}
